import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const inputStyle = {
  width: '100%', padding: '13px 16px', borderRadius: '12px', boxSizing: 'border-box',
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.07)', color: '#f1f5f9',
  fontSize: '15px', fontFamily: "'Sora',sans-serif", outline: 'none',
};

const bgStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: "'Sora',sans-serif", padding: '1rem',
};

const cardStyle = {
  background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
  borderRadius: '24px', padding: '2rem',
  border: '1px solid rgba(255,255,255,0.09)',
  width: '100%', maxWidth: '400px',
};

export default function LoginPage() {
  const [view, setView]         = useState('main'); // 'main' | 'superadmin'
  const [role, setRole]         = useState('');
  const [phone, setPhone]       = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail]       = useState('');
  const [saPass, setSaPass]     = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const { login }               = useAuth();
  const navigate                = useNavigate();

  // ── Main Login Submit ──────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!role) return toast.error('Select your role first');
    setLoading(true);
    try {
      const user = await login(phone, password, role);
      toast.success(`Welcome, ${user.name}! ✓`);
      navigate(role === 'ADMIN' ? '/admin' : '/customer');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  // ── Super Admin Login Submit ───────────────────────────────────────────────
  const handleSALogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, saPass, 'SUPER_ADMIN');
      toast.success('Welcome, Super Admin!');
      navigate('/super-admin');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  // ── SHARED UI PIECES ───────────────────────────────────────────────────────
  const Logo = ({ subtitle }) => (
    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '70px', height: '70px', borderRadius: '20px',
        background: 'linear-gradient(135deg,#F97316,#EF4444)',
        fontSize: '32px', marginBottom: '12px',
        boxShadow: '0 0 40px rgba(249,115,22,0.35)',
      }}>₹</div>
      <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '800', margin: 0 }}>Udhari</h1>
      <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0' }}>{subtitle}</p>
    </div>
  );

  const EyeBtn = ({ show, toggle }) => (
    <button type="button" onClick={toggle} style={{
      position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: 0,
    }}>
      {show ? '🙈' : '👁️'}
    </button>
  );

  // ── SUPER ADMIN VIEW ───────────────────────────────────────────────────────
  if (view === 'superadmin') {
    return (
      <div style={bgStyle}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <Logo subtitle="Digital Local Lending Platform" />
          <div style={cardStyle}>
            <h2 style={{ color: '#f1f5f9', fontSize: '17px', fontWeight: '700', margin: '0 0 1.5rem' }}>
              Super Admin Sign In
            </h2>
            <form onSubmit={handleSALogin}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                  Email Address
                </label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="superadmin@udhari.com" required
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'} value={saPass}
                    onChange={e => setSaPass(e.target.value)} placeholder="••••••••" required
                    style={{ ...inputStyle, paddingRight: '44px' }}
                  />
                  <EyeBtn show={showPass} toggle={() => setShowPass(s => !s)} />
                </div>
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '14px',
                background: loading ? 'rgba(100,116,139,0.25)' : 'linear-gradient(135deg,#F97316,#EF4444)',
                color: loading ? '#475569' : '#fff',
                border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Sora',sans-serif",
              }}>
                {loading ? 'Signing in...' : 'Sign In →'}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={() => { setView('main'); setShowPass(false); }} style={{
                background: 'none', border: 'none', color: '#F97316',
                cursor: 'pointer', fontSize: '13px', fontFamily: "'Sora',sans-serif",
              }}>
                ← Back to main login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN LOGIN VIEW ────────────────────────────────────────────────────────
  const ROLES = [
    { id: 'ADMIN',    label: 'Lender / Shop', icon: '🏪', desc: 'Shop owner lender' },
    { id: 'CUSTOMER', label: 'Borrower',       icon: '👤', desc: 'Local borrower'    },
  ];

  return (
    <div style={bgStyle}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <Logo subtitle="Digital Local Lending Platform" />
        <div style={cardStyle}>
          <h2 style={{ color: '#f1f5f9', fontSize: '17px', fontWeight: '700', margin: '0 0 1.5rem' }}>
            Sign In
          </h2>

          {/* Role Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ color: '#475569', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 10px' }}>
              I am a
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {ROLES.map(r => (
                <button key={r.id} type="button" onClick={() => setRole(r.id)} style={{
                  padding: '14px 10px', borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                  border: role === r.id ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.08)',
                  background: role === r.id ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.03)',
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{r.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: role === r.id ? '#F97316' : '#94a3b8' }}>{r.label}</div>
                  <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px' }}>{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                Phone Number
              </label>
              <input
                type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="10-digit mobile number" maxLength={10}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Your password"
                  style={{ ...inputStyle, paddingRight: '44px' }}
                />
                <EyeBtn show={showPass} toggle={() => setShowPass(s => !s)} />
              </div>
            </div>
            <button type="submit" disabled={loading || !role} style={{
              width: '100%', padding: '14px',
              background: loading || !role ? 'rgba(100,116,139,0.25)' : 'linear-gradient(135deg,#F97316,#EF4444)',
              color: loading || !role ? '#475569' : '#fff',
              border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700',
              cursor: loading || !role ? 'not-allowed' : 'pointer', fontFamily: "'Sora',sans-serif",
            }}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ color: '#475569', fontSize: '13px', margin: 0 }}>
              Want to lend?{' '}
              <Link to="/register/admin" style={{ color: '#F97316', textDecoration: 'none', fontWeight: '700' }}>
                Register as Lender →
              </Link>
            </p>
          </div>
        </div>

        {/* Super Admin subtle link */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button onClick={() => { setView('superadmin'); setShowPass(false); }} style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            color: '#475569', cursor: 'pointer', fontSize: '12px',
            fontFamily: "'Sora',sans-serif", borderRadius: '10px',
            padding: '8px 18px', display: 'inline-flex', alignItems: 'center', gap: '7px',
            transition: 'all 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
          >
            <span style={{ fontSize: '14px' }}>🏛️</span> Platform Administration
          </button>
        </div>
      </div>
    </div>
  );
}
